import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Login from './Login';
import WaveAnimation from './WaveAnimation';
import VoicesCarousel from './VoicesCarousel';
import '../styles/landing.css';
import api from '../api/api';

// Placeholder for hero demo audio - replace with your demo audio URL
const HERO_DEMO_AUDIO_URL = "https://pub-dd660c8124fe47538daccf5895f04878.r2.dev/builtin-voices/0391c30c-7dd2-4901-8e70-c5e0d019c07a.wav"; // Add your audio URL here

const Landing = ({ isAuthenticated, onSignInSuccess, onSignInError }) => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [activeFeature, setActiveFeature] = useState(0);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [voices, setVoices] = useState([]);
  const [voicesLoading, setVoicesLoading] = useState(true);
  const [isHeroAudioPlaying, setIsHeroAudioPlaying] = useState(false);
  const [isHeroAudioLoading, setIsHeroAudioLoading] = useState(false);
  const heroAudioRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchVoices = async () => {
      try {
        setVoicesLoading(true);
        const fetchedVoices = await api.getPublicVoices();
        setVoices(fetchedVoices || []);
      } catch (error) {
        console.error('Error fetching voices:', error);
        setVoices([]);
      } finally {
        setVoicesLoading(false);
      }
    };

    fetchVoices();
  }, []);

  const handleSignInClick = () => {
    setShowSignInModal(true);
  };

  const handleSignInSuccess = (userData) => {
    setShowSignInModal(false);
    if (onSignInSuccess) {
      onSignInSuccess(userData);
    }
  };

  const handleSignInError = (errorMessage) => {
    if (onSignInError) {
      onSignInError(errorMessage);
    }
  };

  const handleHeroAudioToggle = () => {
    if (!HERO_DEMO_AUDIO_URL) {
      console.warn('Hero demo audio URL not configured');
      return;
    }

    const audio = heroAudioRef.current;
    if (!audio) return;

    if (isHeroAudioPlaying) {
      // Pause audio
      audio.pause();
      setIsHeroAudioPlaying(false);
    } else {
      // Play audio
      if (!audio.src) {
        audio.src = HERO_DEMO_AUDIO_URL;
      }

      setIsHeroAudioLoading(true);
      audio.play()
        .then(() => {
          setIsHeroAudioLoading(false);
          setIsHeroAudioPlaying(true);
        })
        .catch((error) => {
          console.error('Error playing hero audio:', error);
          setIsHeroAudioLoading(false);
          setIsHeroAudioPlaying(false);
        });
    }
  };

  const handleHeroAudioEnded = () => {
    setIsHeroAudioPlaying(false);
    setIsHeroAudioLoading(false);
  };

  const features = [
    {
      icon: '🎙️',
      title: 'Natural Voice',
      description: 'Convert text to speech with AI-powered natural-sounding voices'
    },
    {
      icon: '🔐',
      title: 'Secure',
      description: 'Enterprise-grade security with API key management'
    },
    {
      icon: '🎬',
      title: 'Unlimited Voice Cloning',
      description: 'Clone unlimited voices at no extra charge. Create custom voice profiles effortlessly'
    },
    {
      icon: '🚀',
      title: 'API Ready',
      description: 'Easy integration with REST API and code examples'
    }
  ];

  const starterCredits = 150000;

  const pricingPlans = [
    {
      name: 'Free',
      credits: '2,000 Characters',
      price: 'Free',
      value: 'Get started',
      features: ['2,000 free characters', 'Unlimited voice cloning', 'API access', 'Lifetime credits'],
      popular: false,
      bonus: null
    },
    {
      name: 'Starter',
      credits: '150,000 Characters',
      price: '$10',
      value: 'Best for casual users',
      features: ['150,000 characters', 'Unlimited voice cloning', 'API access', 'Lifetime credits'],
      popular: false,
      bonus: null
    },
    {
      name: 'Creator',
      credits: '400,000 Characters',
      price: '$20',
      value: 'Most popular',
      features: ['400,000 characters', 'Unlimited voice cloning', 'API access', 'Lifetime credits'],
      popular: true,
      bonus: Math.round(((400000 - starterCredits * 2) / (starterCredits * 2)) * 100)
    },
    {
      name: 'Professional',
      credits: '1.1M Characters',
      price: '$50',
      value: 'For heavy users',
      features: ['1.1 million characters', 'Unlimited voice cloning', 'API access', 'Lifetime credits'],
      popular: false,
      bonus: Math.round(((1100000 - starterCredits * 5) / (starterCredits * 5)) * 100)
    }
  ];

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/generate');
    } else {
      handleSignInClick();
    }
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="nav-container">
          <div className="nav-logo" onClick={() => {
            navigate('/');
            window.scrollTo(0, 0);
          }} style={{ cursor: 'pointer' }}>
            <img src="/logo.png" alt="Stick Audio Logo" className="logo-image" />
            <span className="logo-text">Stick Audio</span>
          </div>
          <div className="nav-links">
            <a href="/docs" className="nav-link">API Docs</a>
            <a href="/blogs" className="nav-link">Blog</a>
            <button className="nav-btn primary" onClick={handleGetStarted}>
            {isAuthenticated ? 'Go to App' : 'Sign In'}
          </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="animated-blob blob-1"></div>
          <div className="animated-blob blob-2"></div>
          <div className="animated-blob blob-3"></div>
        </div>

        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Transform Text into<br />
              <span className="hero-title-line">
                <span className="gradient-text">Stunning Audio</span>
                <span className="hero-mobile-wave-animation">
                  <WaveAnimation
                    size="small"
                    animated={true}
                    onPlayClick={handleHeroAudioToggle}
                    isPlaying={isHeroAudioPlaying}
                    isLoading={isHeroAudioLoading}
                  />
                </span>
              </span>
            </h1>
            <p className="hero-subtitle">
              The most powerful text-to-speech platform with AI-driven voices,
              intelligent voice customization, and enterprise-grade features.
            </p>
            <div className="hero-buttons">
              <button className="btn btn-primary btn-lg" onClick={handleGetStarted}>
                Start Creating Now
                <span className="btn-arrow">→</span>
              </button>
              <div className="hero-pricing-badge">
                <span className="badge-star">⭐</span>
                <div className="badge-content">
                  <span className="badge-multiplier">5x</span>
                  <div className="badge-text">
                    <div>Cheaper Than Eleven Labs</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">🎤</span>
                <span className="stat-label">Unlimited Voice Cloning</span>
                <div className="stat-border"></div>
              </div>
              <div className="stat">
                <span className="stat-number">💳</span>
                <span className="stat-label">Pay as You Go</span>
                <div className="stat-border"></div>
              </div>
              <div className="stat">
                <span className="stat-number">♾️</span>
                <span className="stat-label">Credits Never Expire</span>
                <div className="stat-border"></div>
              </div>
            </div>
          </div>

          <div className="hero-visual">
            <div className="audio-visualization">
              <div className="wave wave-1"></div>
              <div className="wave wave-2"></div>
              <div className="wave wave-3"></div>
              <div className="wave wave-4"></div>
              <div className="wave wave-5"></div>
              <div className="wave wave-6"></div>
              <div className="wave wave-7"></div>
              <div className="wave wave-8"></div>
              <div className="wave wave-9"></div>
              <div className="wave wave-10"></div>
              <div className="wave wave-11"></div>
              <div className="wave wave-12"></div>
              <div className="wave wave-13"></div>
              <div className="wave wave-14"></div>
              <div className="wave wave-15"></div>
              <div className="wave wave-16"></div>
              <div className="wave wave-17"></div>
              <button
                className="play-button"
                onClick={handleHeroAudioToggle}
                disabled={isHeroAudioLoading || !HERO_DEMO_AUDIO_URL}
                aria-label={isHeroAudioPlaying ? 'Pause demo audio' : 'Play demo audio'}
              >
                {isHeroAudioLoading ? (
                  <span className="loading-spinner-hero"></span>
                ) : isHeroAudioPlaying ? (
                  <span>⏸</span>
                ) : (
                  <span>▶</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Voices Carousel Section */}
      <section className="voices-section">
        <div className="section-header">
          <h2>Explore Our Voices</h2>
          <p>Listen to sample voices and find the perfect match for your content</p>
        </div>

        {voicesLoading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
            Loading voices...
          </div>
        ) : voices.length > 0 ? (
          <VoicesCarousel voices={voices} />
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
            No voices available at the moment
          </div>
        )}
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="section-header">
          <h2>Powerful Features</h2>
          <p>Everything you need to create amazing audio</p>
        </div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`feature-card ${activeFeature === index ? 'active' : ''}`}
              onMouseEnter={() => setActiveFeature(index)}
            >
              <div className="feature-header">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
              </div>
              <p>{feature.description}</p>
              <div className="feature-border"></div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="section-header">
          <h2>Pay-As-You-Go Pricing - <span className="highlight-no-sub">No Subscription</span></h2>
          <p>Flexible credit packages. Credits never expire. Use them on web or API.</p>
        </div>

        <div className="pricing-grid">
          {pricingPlans.map((plan, index) => (
            <div key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
              {plan.popular && <div className="popular-badge">MOST POPULAR</div>}
              <div className="pricing-card-header">
                <h3>{plan.name}</h3>
                {plan.bonus !== null && (
                  <div className="bonus-badge-landing">
                    <span>+{plan.bonus}% Bonus</span>
                  </div>
                )}
              </div>
              <div className="pricing-value">{plan.value}</div>
              <div className="pricing-price">{plan.price}</div>
              <div className="pricing-credits">{plan.credits}</div>
              <ul className="pricing-features">
                {plan.features.map((feature, idx) => (
                  <li key={idx}>
                    <span className="check-icon">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
              <button className={`btn ${plan.popular ? 'btn-primary' : 'btn-secondary'} btn-block`} onClick={handleGetStarted}>
                Get Started
              </button>
            </div>
          ))}
        </div>

        <div className="pricing-faq-notice">
          <p>💡 <strong>Questions about credits?</strong> Check our FAQ or reach out to support. We also offer custom enterprise packages.</p>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta-section" className="cta-section">
        <div className="cta-content">
          <h2>Ready to Create Amazing Audio?</h2>
          <p>Join thousands of creators, developers, and businesses using Stick Audio</p>
          <div className="cta-buttons">
            <button className="btn btn-primary btn-lg" onClick={handleGetStarted}>
              Start Free Trial
            </button>
            <button className="btn btn-secondary btn-lg" onClick={handleGetStarted}>
              View Demo
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="section-header">
          <h2>Frequently Asked Questions</h2>
        </div>

        <div className="faq-grid">
          <div className="faq-item">
            <div className="faq-header">
              <h4>📊 How do credits work?</h4>
            </div>
            <p>Get 2,000 free characters on signup. 1 credit = 1 character. Choose from our flexible credit packages: $10 for 150K characters, $20 for 400K, or $50 for 1.1M. Credits never expire and work across both web and API.</p>
            <div className="faq-border"></div>
          </div>
          <div className="faq-item">
            <div className="faq-header">
              <h4>🎙️ Can I use custom voices?</h4>
            </div>
            <p>Yes! Upload your own voice samples and use them alongside our default voices. You can also clone your voice or create custom voice profiles.</p>
            <div className="faq-border"></div>
          </div>
          <div className="faq-item">
            <div className="faq-header">
              <h4>🔑 What's included in the API access?</h4>
            </div>
            <p>Generate API keys for programmatic access. Support for both Bearer tokens and X-API-Key headers with full documentation and examples.</p>
            <div className="faq-border"></div>
          </div>
          <div className="faq-item">
            <div className="faq-header">
              <h4>💰 How 5x cheaper than 11 Labs?</h4>
            </div>
            <p>We've optimized our infrastructure and pricing model to pass savings directly to you. Same high-quality voices, better value. Whether you use the web app or API, you get the best rates in the industry.</p>
            <div className="faq-border"></div>
          </div>
          <div className="faq-item">
            <div className="faq-header">
              <h4>⏰ Do credits expire?</h4>
            </div>
            <p>Nope! Your credits never expire. Use them whenever you need. They work seamlessly across both the web app and our REST API.</p>
            <div className="faq-border"></div>
          </div>
          <div className="faq-item">
            <div className="faq-header">
              <h4>🔗 Can I integrate with my app?</h4>
            </div>
            <p>Yes! Our REST API makes integration simple. We provide code examples in cURL, Node.js, and Python.</p>
            <div className="faq-border"></div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Stick Audio</h4>
            <p>Transform text into stunning audio with AI-powered voices.</p>
          </div>
          <div className="footer-section">
            <h4>Product</h4>
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
          </div>
          <div className="footer-section">
            <h4>Company</h4>
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}>Privacy</a>
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/contact-us'); }}>Contact Us</a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2024 Stick Audio. All rights reserved.</p>
        </div>
      </footer>

      {/* Sign In Modal */}
      {showSignInModal && (
        <div className="signin-modal-overlay" onClick={() => setShowSignInModal(false)}>
          <div className="signin-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="signin-modal-close"
              onClick={() => setShowSignInModal(false)}
            >
              ×
            </button>
            <div className="signin-modal-header">
              <h2>🎵 Join Stick Audio</h2>
              <p>Start creating amazing audio today</p>
            </div>
            <div className="signin-wrapper">
              <Login
                onSignInSuccess={handleSignInSuccess}
                onSignInError={handleSignInError}
              />
            </div>
          </div>
        </div>
      )}

      {/* Hidden audio element for hero demo */}
      <audio
        ref={heroAudioRef}
        onEnded={handleHeroAudioEnded}
        preload="none"
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default Landing;
